// React error boundary mounted around <Outlet/> in RootLayout. Renders
// a design-system-styled fallback inside main-content (sidebar stays
// visible) and reports the error via lib/error-tracking.

import React from 'react';
import { reportError } from '@/lib/error-tracking';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
  showDetails: boolean;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, componentStack: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    reportError(error, {
      source: 'error-boundary',
      componentStack: info.componentStack ?? undefined,
    });
  }

  render() {
    const { error, componentStack, showDetails } = this.state;
    const { children } = this.props;
    if (!error) return children;

    return (
      <div className="error-boundary-fallback">
        <div className="error-boundary-icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1 className="header3">Something went wrong</h1>

        <p className="body2 text-secondary">
          This screen ran into an unexpected error. Reload the page to try again.
          If the problem persists, contact support and include the technical details below.
        </p>

        <div className="error-boundary-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => { window.location.href = '/'; }}
          >
            Back to home
          </button>
        </div>

        <button
          type="button"
          className="error-boundary-toggle"
          onClick={() => this.setState({ showDetails: !showDetails })}
          aria-expanded={showDetails}
        >
          {showDetails ? '▼' : '▶'}
          {' '}
          Technical details
        </button>

        {showDetails && (
          <div className="error-boundary-details">
            <div className="error-boundary-message">
              <span className="eyebrow">Error</span>
              <code>{error.message || error.name || 'Unknown error'}</code>
            </div>
            {error.stack && (
              <div className="error-boundary-stack">
                <span className="eyebrow">Stack</span>
                <pre>{error.stack}</pre>
              </div>
            )}
            {componentStack && (
              <div className="error-boundary-stack">
                <span className="eyebrow">Component tree</span>
                <pre>{componentStack}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default AppErrorBoundary;
